import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ScadViewer } from '@/views/ScadViewer';
import { ExamplesToolbar } from '@/components/examples/ExamplesToolbar';
import { EXAMPLES, getExampleById } from '@/examples';
import type { Example } from '@/examples/types';

/**
 * Route: /playground/examples
 *        /playground/examples/:id
 *
 * Wraps the full ScadViewer with a custom slideshow toolbar instead of the
 * regular EditorToolbar. The :id param identifies the current example.
 * Navigating to /examples (no id) redirects to the first example.
 */
export function ExamplesViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Resolve current example from URL param
  const currentExample: Example | undefined = id
    ? getExampleById(id)
    : EXAMPLES[0];

  // If there are examples but the URL has no id (or an invalid id), redirect
  // to the first example so the URL stays canonical.
  useEffect(() => {
    if (EXAMPLES.length === 0) return;

    if (!id) {
      // /examples → /examples/:firstId
      navigate(`/examples/${EXAMPLES[0].id}`, { replace: true });
      return;
    }

    if (!getExampleById(id)) {
      // Unknown id — fall back to first example
      navigate(`/examples/${EXAMPLES[0].id}`, { replace: true });
    }
  }, [id, navigate]);

  const handleSelectExample = useCallback(
    (exampleId: string) => {
      navigate(`/examples/${exampleId}`);
    },
    [navigate],
  );

  const toolbar = (
    <ExamplesToolbar
      currentExample={currentExample}
      onSelectExample={handleSelectExample}
    />
  );

  return (
    <ScadViewer
      key={currentExample?.id ?? 'examples-empty'}
      toolbarOverride={toolbar}
      initialScadCode={currentExample?.scadCode}
    />
  );
}
